import { DateTime } from 'luxon';
import { parseJsonRecovering } from '../../shared/utils/jsonRecovery';

/**
 * Custom form questions shown as list columns. A view saves each one as a ViewColumn whose FormQuestion is
 * "<TemplateFormId>:<input id>"; its ColumnKey is derived from that, above the custom property band
 * (1000 + property id), so one question has one key in every view and in the saved column widths and order.
 */
export const FORM_COLUMN_BASE = 2_000_000;

export const isFormColumnKey = (key: number | string) => Number(key) >= FORM_COLUMN_BASE;

/** A column key in the custom property band - not a built-in column and not a form question. */
export const isCustomPropertyColumnKey = (key: number | string) => Number(key) >= 1000 && !isFormColumnKey(key);

export const formQuestionId = (templateFormId: number, inputId: string) => `${templateFormId}:${inputId}`;

// FNV-1a: stable across sessions and browsers, which a key saved in a view has to be.
export const formColumnKey = (formQuestion: string) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < formQuestion.length; i++) {
    hash ^= formQuestion.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return FORM_COLUMN_BASE + (hash % 1_000_000_000);
};

export interface FormQuestionChoice { title: string; value: string; }

export interface FormQuestion {
  key: number;
  formQuestion: string;
  templateFormId: number;
  inputId: string;
  label: string;
  type: string;
  choices?: FormQuestionChoice[];
  /** A choice question that takes several answers - drawn as pills. */
  multiSelect?: boolean;
}

export interface TemplateFormDefinition {
  Id: number;
  Name?: string;
  Order?: number;
  Form?: { Name?: string; Definition?: string | object };
}

interface CardElement {
  type?: string;
  id?: string;
  label?: string;
  title?: string;
  isCustomProperty?: boolean;
  choices?: FormQuestionChoice[];
  isMultiSelect?: boolean;
}

/** Unreadable JSON is treated as absent: one broken blob must not take a row, or the list, with it. */
const readJson = (json: string): unknown => {
  try {
    return parseJsonRecovering(json);
  } catch {
    return undefined;
  }
};

const ANSWERABLE = ['Input.Text', 'Input.Number', 'Input.Date', 'Input.Time', 'Input.Toggle', 'Input.ChoiceSet'];

const cardInputs = (node: unknown, found: CardElement[] = []): CardElement[] => {
  if (Array.isArray(node)) node.forEach(child => cardInputs(child, found));
  else if (node && typeof node === 'object') {
    const element = node as CardElement;
    if (typeof element.type === 'string' && typeof element.id === 'string') found.push(element);
    Object.values(node).forEach(child => cardInputs(child, found));
  }
  return found;
};

/**
 * The questions a template's forms ask, in form order then card order, as "Form name: Question". Ticket fields
 * (Ticket.*) and custom-property-bound questions are left out - they already have columns.
 */
export const templateFormQuestions = (templateForms: TemplateFormDefinition[]): FormQuestion[] =>
  [...(templateForms ?? [])].sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0)).flatMap(templateForm => {
    const definition = typeof templateForm.Form?.Definition === 'string'
      ? readJson(templateForm.Form.Definition)
      : templateForm.Form?.Definition;
    if (!definition || typeof definition !== 'object') return [];
    const formName = templateForm.Name || templateForm.Form?.Name || '';
    const seen = new Set<string>();
    return cardInputs((definition as { body?: unknown }).body)
      .filter(element => ANSWERABLE.includes(element.type) && !element.isCustomProperty && !element.id.startsWith('Ticket.'))
      .filter(element => !seen.has(element.id) && seen.add(element.id))
      .map(element => {
        const formQuestion = formQuestionId(templateForm.Id, element.id);
        const question = element.label || element.title || element.id;
        return {
          key: formColumnKey(formQuestion),
          formQuestion,
          templateFormId: templateForm.Id,
          inputId: element.id,
          label: formName ? `${formName}: ${question}` : question,
          type: element.type,
          choices: element.choices,
          multiSelect: element.type === 'Input.ChoiceSet' && !!element.isMultiSelect,
        };
      });
  });

export interface TicketFormAnswers { TemplateFormId: number; CardAnswerJson?: string; IsDeleted?: boolean; }

const answerOf = (question: FormQuestion, forms: TicketFormAnswers[]): string => {
  const form = forms?.find(f => f.TemplateFormId === question.templateFormId && !f.IsDeleted);
  if (!form?.CardAnswerJson) return '';
  const answers = readJson(form.CardAnswerJson);
  const value = answers && typeof answers === 'object' ? (answers as Record<string, unknown>)[question.inputId] : undefined;
  if (value == null) return '';
  return typeof value === 'string' ? value : JSON.stringify(value);
};

/**
 * A ticket's answer to a question as the list shows it: dates and times in the user's locale (the date as it was
 * picked - the stored offset is the requester's, so converting it would move the day), choices by their titles,
 * a toggle as True / False or its form's own values (toggleText). Blank when the ticket never answered that form.
 */
export interface AnswerTextOptions { trueText: string; falseText: string; /** As ConvertDateToLocale: the browser's language. */ locale: string; }

export const formAnswerText = (question: FormQuestion, forms: TicketFormAnswers[], options: AnswerTextOptions): string => {
  const answer = answerOf(question, forms).trim();
  if (!answer) return '';
  switch (question.type) {
    case 'Input.Date': {
      const date = DateTime.fromISO(answer.slice(0, 10)).setLocale(options.locale);
      return date.isValid ? date.toLocaleString(DateTime.DATE_SHORT) : answer;
    }
    case 'Input.Time': {
      const time = DateTime.fromFormat(answer.slice(0, 5), 'HH:mm').setLocale(options.locale);
      return time.isValid ? time.toLocaleString(DateTime.TIME_SIMPLE) : answer;
    }
    case 'Input.Toggle':
      return toggleText(answer, options);
    case 'Input.ChoiceSet':
      return formAnswerChoices(question, forms).join(', ');
    default:
      return answer;
  }
};

/**
 * A toggle on the default values reads True / False, as a True/False custom property's column does; one whose form
 * sets its own "Value when on" / "off" ("yes" / "no") shows those as stored, as the CSV export writes them.
 */
const toggleText = (answer: string, options: AnswerTextOptions): string => {
  const value = answer.toLowerCase();
  if (value === 'true') return options.trueText;
  if (value === 'false') return options.falseText;
  return answer;
};

/** A choice question's answer as its choices' titles, in the order answered - a value no longer offered as stored. */
export const formAnswerChoices = (question: FormQuestion, forms: TicketFormAnswers[]): string[] =>
  answerOf(question, forms).split(',').map(value => value.trim()).filter(Boolean)
    .map(value => question.choices?.find(choice => choice.value === value)?.title ?? value);

/**
 * The one template a view's Template filter names, or null. Form question columns exist only then: two templates,
 * or "No Template" (0), have no shared questions.
 */
export const singleTemplateId = (selectedKeys: (string | number)[]): number | null => {
  const keys = selectedKeys.map(key => String(key).trim()).filter(key => key !== '');
  return keys.length === 1 && Number(keys[0]) > 0 ? Number(keys[0]) : null;
};

// Fixed wording and locale: only whether two answers read differently matters here, not how they read.
const COMPARE: AnswerTextOptions = { trueText: 'true', falseText: 'false', locale: 'en-US' };

/**
 * The question columns an answer save changed - compared as the cells show them, so a date saved again with another
 * time offset but the same day is not a change. None in the view: the column it was opened from, so the save shows
 * its outcome somewhere.
 */
export const changedFormColumns = (questions: FormQuestion[], before: TicketFormAnswers[], after: TicketFormAnswers[], openedFrom: number): number[] => {
  const changed = questions.filter(question => formAnswerText(question, before, COMPARE) !== formAnswerText(question, after, COMPARE)).map(question => question.key);
  return changed.length > 0 ? changed : [openedFrom];
};
